import { CheckCircleIcon, FunnelSimpleIcon } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';

import { Drawer } from '@/components/feedback/drawer';
import { EmptyState } from '@/components/feedback/empty-state';
import { formatIsoDate } from '@/lib/utils/format';

import { useDeviceHistory } from '../hooks';
import type { ActivityEvent } from '../types';

import styles from './history-drawer.module.scss';
import {
  EMPTY_ACTIVITY_FILTER,
  HistoryFilterDrawer,
  type ActivityFilter,
} from './history-filter-drawer';

function filterEvents(events: ActivityEvent[], f: ActivityFilter): ActivityEvent[] {
  return events.filter((e) => {
    if (f.types.length && !f.types.includes(e.type)) return false;
    if (f.from && e.timestamp < f.from) return false;
    if (f.to && e.timestamp > f.to) return false;
    return true;
  });
}

export type HistoryDrawerProps = {
  open: boolean;
  deviceId: string;
  onClose: () => void;
};

/** Activity-log drawer (Figma 2249-675428): a green Activity Log card with a Live pill,
 *  then a check-circle timeline (olive date, title, detail per event). */
export function HistoryDrawer({ open, deviceId, onClose }: HistoryDrawerProps) {
  const { data } = useDeviceHistory(deviceId);
  const [filter, setFilter] = useState<ActivityFilter>(EMPTY_ACTIVITY_FILTER);
  const [filterOpen, setFilterOpen] = useState(false);

  const events = useMemo(() => filterEvents(data ?? [], filter), [data, filter]);

  return (
    <Drawer open={open} onClose={onClose} title="History" cancelText="Close" onCancel={onClose}>
      <div className={styles.toolbar}>
        <button
          type="button"
          className={styles.filterBtn}
          aria-label="Filter history"
          onClick={() => setFilterOpen(true)}
        >
          <FunnelSimpleIcon size={18} />
        </button>
      </div>

      <div className={styles.activityCard}>
        <div className={styles.activityTop}>
          <span className={styles.activityLabel}>Activity Log</span>
          <span className={styles.livePill}>
            Live
            <span className={styles.liveDot} />
          </span>
        </div>
        <span className={styles.activityCount}>{events.length} Events</span>
        <span className={styles.activitySub}>Past 7 days activity monitor</span>
      </div>

      {events.length > 0 ? (
        <ul className={styles.timeline}>
          {events.map((e, i) => (
            <li
              key={e.id}
              className={[styles.item, i === events.length - 1 ? styles.itemLast : undefined]
                .filter(Boolean)
                .join(' ')}
            >
              <span className={styles.dot}>
                <CheckCircleIcon size={20} weight="regular" />
              </span>
              <div className={styles.itemBody}>
                <span className={styles.date}>{formatIsoDate(e.timestamp)}</span>
                <span className={styles.itemTitle}>{e.title}</span>
                <span className={styles.itemDetail}>{e.detail}</span>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState title="No activity" description="No events match the selected filters." />
      )}

      <HistoryFilterDrawer
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApply={(next) => {
          setFilter(next);
          setFilterOpen(false);
        }}
      />
    </Drawer>
  );
}
